import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Card, Row, Col, Timeline, Spin, Input, Button } from "antd";
import { CoffeeOutlined, SearchOutlined, TeamOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";
import "./AttendancePage.scss";
import { fetchAttendance, updateBreak } from "../../store/attendanceSlice";
import { getUserType } from "../../assets/helpers";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (id) => {
  const colors = [
    "#f56a00", // Orange
    "#7265e6", // Purple
    "#ffbf00", // Yellow
    "#00a2ae", // Cyan
    "#2f54eb", // Indigo
    "#eb2f96", // Pink
    "#52c41a", // Green
    "#13c2c2", // Teal
  ];
  return colors[(id || 0) % colors.length];
};

const AttendancePage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { attendance_list, loading } = useSelector((state) => state.attendance);
  const [filterDate, setFilterDate] = useState(() => dayjs());
  const [selectedDate, setSelectedDate] = useState(null);

  // Agency-specific state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTelecallerId, setSelectedTelecallerId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const userType = getUserType(user);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterDate]);

  useEffect(() => {
    const params = {};
    if (filterDate) {
      params.date = filterDate.format("YYYY-MM-DD");
    }
    dispatch(fetchAttendance(params));
  }, [dispatch, filterDate]);

  // Merge or select between backend list and demo data (Telecaller view)
  const displayList = useMemo(() => {
    let apiRecords = [];

    if (attendance_list && Array.isArray(attendance_list.telecallers)) {
      apiRecords = attendance_list.telecallers;
    } else if (Array.isArray(attendance_list)) {
      apiRecords = attendance_list;
    } else if (attendance_list && typeof attendance_list === "object") {
      if (Array.isArray(attendance_list.results)) {
        apiRecords = attendance_list.results;
      } else if (Array.isArray(attendance_list.data)) {
        apiRecords = attendance_list.data;
      } else if (attendance_list.date || attendance_list.user_id) {
        apiRecords = [attendance_list];
      }
    }

    if (apiRecords.length > 0) {
      return apiRecords.map((item, index) => {
        let checkIn = "-";
        let checkOut = "-";
        const sessions = item.attendance?.work_sessions || [];

        if (sessions.length > 0) {
          const firstSession = sessions[0];
          const lastSession = sessions[sessions.length - 1];
          if (firstSession?.login_time) {
            checkIn = moment(firstSession.login_time).format("hh:mm A");
          }
          if (lastSession?.logout_time) {
            checkOut = moment(lastSession.logout_time).format("hh:mm A");
          } else if (lastSession?.is_active) {
            checkOut = "Active";
          }
        }

        // Calculate work hours by summing work sessions duration
        let workHours = "-";
        if (item.totals?.total_work_duration || item.attendance?.total_work_duration) {
          workHours = item.totals?.total_work_duration || item.attendance?.total_work_duration;
        } else if (sessions.length > 0) {
          const totalWorkSeconds = sessions.reduce((acc, s) => acc + (s.duration_seconds || 0), 0);
          if (totalWorkSeconds > 0) {
            const hours = Math.floor(totalWorkSeconds / 3600);
            const minutes = Math.floor((totalWorkSeconds % 3600) / 60);
            workHours = `${hours}h ${minutes}m`;
          }
        }

        // Calculate break duration by summing breaks duration
        let breakDuration = "-";
        const breaks = item.attendance?.breaks || [];
        if (item.totals?.total_break_duration || item.attendance?.total_break_duration) {
          breakDuration = item.totals?.total_break_duration || item.attendance?.total_break_duration;
        } else if (breaks.length > 0) {
          const totalBreakSeconds = breaks.reduce((acc, b) => acc + (b.duration_seconds || 0), 0);
          if (totalBreakSeconds > 0) {
            const hours = Math.floor(totalBreakSeconds / 3600);
            const minutes = Math.floor((totalBreakSeconds % 3600) / 60);
            breakDuration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
          } else {
            breakDuration = "0m";
          }
        }

        let status = "Present";
        const backendStatus = String(item.attendance_status || "").toLowerCase();
        if (backendStatus === "weekly_off") {
          status = "Weekly Off";
        } else if (backendStatus === "leave" || backendStatus === "on_leave") {
          status = "On Leave";
        } else if (backendStatus === "inactive" || !item.attendance) {
          status = "Absent";
        } else if (backendStatus === "active") {
          status = "Present";
        } else {
          status = item.attendance_status || "Present";
        }

        return {
          key: item.attendance?.attendance_id || `real-${index}`,
          date: item.date,
          displayDate: moment(item.date).format("DD MMM YYYY"),
          day: moment(item.date).format("dddd"),
          checkIn,
          checkOut,
          workHours,
          breakDuration,
          status,
        };
      });
    }

    return [];
  }, [attendance_list]);

  // Agency list filter
  const filteredList = useMemo(() => {
    let apiRecords = [];
    if (attendance_list && Array.isArray(attendance_list.telecallers)) {
      apiRecords = attendance_list.telecallers;
    } else if (Array.isArray(attendance_list)) {
      apiRecords = attendance_list;
    } else if (attendance_list && typeof attendance_list === "object") {
      if (Array.isArray(attendance_list.results)) {
        apiRecords = attendance_list.results;
      } else if (Array.isArray(attendance_list.data)) {
        apiRecords = attendance_list.data;
      } else if (attendance_list.date || attendance_list.user_id) {
        apiRecords = [attendance_list];
      }
    }

    if (!searchQuery) return apiRecords;

    const query = searchQuery.toLowerCase().trim();
    return apiRecords.filter((item) => {
      const nameMatch = String(item.telecaller_name || "").toLowerCase().includes(query);
      const idMatch = String(item.telecaller_id || "").includes(query);
      const userIdMatch = String(item.user_id || "").includes(query);
      return nameMatch || idMatch || userIdMatch;
    });
  }, [attendance_list, searchQuery]);

  const paginatedList = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredList.slice(startIndex, endIndex);
  }, [filteredList, currentPage]);

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);

  // Track currently selected raw record from API list or demo list
  const selectedRawRecord = useMemo(() => {
    let apiRecords = [];
    if (attendance_list && Array.isArray(attendance_list.telecallers)) {
      apiRecords = attendance_list.telecallers;
    } else if (Array.isArray(attendance_list)) {
      apiRecords = attendance_list;
    } else if (attendance_list && typeof attendance_list === "object") {
      if (Array.isArray(attendance_list.results)) {
        apiRecords = attendance_list.results;
      } else if (Array.isArray(attendance_list.data)) {
        apiRecords = attendance_list.data;
      } else if (attendance_list.date || attendance_list.user_id) {
        apiRecords = [attendance_list];
      }
    }

    if (userType === "agency" || userType === "supervisor") {
      if (filteredList.length === 0) return null;
      if (selectedTelecallerId) {
        return filteredList.find((r) => r.telecaller_id === selectedTelecallerId) || filteredList[0];
      }
      return filteredList[0];
    } else {
      const targetDate = selectedDate || displayList[0]?.date;
      if (!targetDate) return null;

      if (apiRecords.length > 0) {
        return apiRecords.find((r) => r.date === targetDate) || apiRecords[0];
      }
      return null;
    }
  }, [attendance_list, displayList, selectedDate, selectedTelecallerId, userType, filteredList]);

  // Compute agency statistics
  const agencyStats = useMemo(() => {
    let apiRecords = [];
    if (attendance_list && Array.isArray(attendance_list.telecallers)) {
      apiRecords = attendance_list.telecallers;
    } else if (Array.isArray(attendance_list)) {
      apiRecords = attendance_list;
    } else if (attendance_list && typeof attendance_list === "object") {
      if (Array.isArray(attendance_list.results)) {
        apiRecords = attendance_list.results;
      } else if (Array.isArray(attendance_list.data)) {
        apiRecords = attendance_list.data;
      } else if (attendance_list.date || attendance_list.user_id) {
        apiRecords = [attendance_list];
      }
    }

    const total = apiRecords.length;
    const active = apiRecords.filter(
      (item) => String(item.attendance_status || "").toLowerCase() === "active"
    ).length;
    const inactive = apiRecords.filter(
      (item) => String(item.attendance_status || "").toLowerCase() === "inactive" || !item.attendance
    ).length;

    return { total, active, inactive };
  }, [attendance_list]);

  // Compute chronological timeline events for selected day
  const timelineEvents = useMemo(() => {
    if (!selectedRawRecord) return [];

    // Process real API work sessions and breaks chronologically
    const events = [];
    const sessions = selectedRawRecord.attendance?.work_sessions || [];
    const breaks = selectedRawRecord.attendance?.breaks || [];

    sessions.forEach((s) => {
      if (s.login_time) {
        events.push({
          timeVal: moment(s.login_time),
          timeStr: moment(s.login_time).format("hh:mm A"),
          type: "login",
          title: "Check-in",
          desc: `Login session (Duration: ${s.duration || "-"})`,
        });
      }
      if (s.logout_time) {
        events.push({
          timeVal: moment(s.logout_time),
          timeStr: moment(s.logout_time).format("hh:mm A"),
          type: "logout",
          title: "Check-out",
          desc: `Logged out`,
        });
      } else if (s.is_active) {
        events.push({
          timeVal: moment(),
          timeStr: "Active",
          type: "active",
          title: "Current Session",
          desc: `Active session (Running: ${s.duration || "-"})`,
        });
      }
    });

    breaks.forEach((b) => {
      if (b.start_time) {
        events.push({
          timeVal: moment(b.start_time),
          timeStr: moment(b.start_time).format("hh:mm A"),
          type: "break_start",
          title: b.break_type === "lunch" ? "Lunch Break" : "Tea Break",
          desc: `Started ${b.break_type} break`,
        });
      }
      if (b.end_time) {
        events.push({
          timeVal: moment(b.end_time),
          timeStr: moment(b.end_time).format("hh:mm A"),
          type: "break_end",
          title: b.break_type === "lunch" ? "Lunch Break" : "Tea Break",
          desc: `Ended break (Duration: ${b.duration || "-"})`,
        });
      }
    });

    // Sort events by timestamp
    events.sort((a, b) => {
      if (a.timeVal && b.timeVal) {
        return a.timeVal.valueOf() - b.timeVal.valueOf();
      }
      return 0;
    });

    return events;
  }, [selectedRawRecord]);

  const currentDisplayDate = useMemo(() => {
    if (userType === "agency" || userType === "supervisor") {
      if (!selectedRawRecord) return "";
      return moment(selectedRawRecord.date).format("DD MMM YYYY");
    } else {
      const targetDate = selectedDate || displayList[0]?.date;
      const match = displayList.find(r => r.date === targetDate);
      return match ? match.displayDate : "";
    }
  }, [displayList, selectedDate, selectedRawRecord, userType]);

  const selectedTelecallerName = (userType === "agency" || userType === "supervisor") ? (selectedRawRecord?.telecaller_name || "") : "";

  // Render Agency Dashboard View
  if (userType === "agency" || userType === "supervisor") {
    return (
      <div className="attendancePageContainer agencyPageContainer animate-fade-in">
        <div className="attendanceHeader">
          <h1 className="attendanceTitle">Telecaller Attendance Overview</h1>
          <div className="attendanceFilters">
            {/* <Input
              placeholder="Search by name or ID..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="attendanceSearchInput"
              allowClear
            /> */}
            <DatePicker
              value={filterDate}
              onChange={(date) => setFilterDate(date)}
              disabledDate={(current) => current && current > dayjs().endOf("day")}
              placeholder="Select Date"
              format="DD MMM YYYY"
              className="attendanceDatePicker"
            />
          </div>
        </div>

        {/* Stats Panel */}
        <div className="agencyStatsRow">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card className="agencyStatCard total" bordered={false}>
                <div className="statIconWrapper">
                  <TeamOutlined />
                </div>
                <div className="statDetails">
                  <span className="statValue">{agencyStats.total}</span>
                  <span className="statLabel">Total Telecallers</span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card className="agencyStatCard present" bordered={false}>
                <div className="statIconWrapper">
                  <CheckCircleOutlined />
                </div>
                <div className="statDetails">
                  <span className="statValue">{agencyStats.active}</span>
                  <span className="statLabel">Present / Active</span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card className="agencyStatCard absent" bordered={false}>
                <div className="statIconWrapper">
                  <CloseCircleOutlined />
                </div>
                <div className="statDetails">
                  <span className="statValue">{agencyStats.inactive}</span>
                  <span className="statLabel">Absent / Inactive</span>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Grid and Activity Timeline */}
        <Row gutter={[24, 24]} className="attendanceGridContainer">
          <Col xs={24} xl={16}>
            <Card className="attendanceTableCard" bordered={false}>
              <div className="tableTitle">Telecaller Attendance Logs</div>
              {loading ? (
                <div className="loadingContainer">
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  <div className="dailyListContainer agencyList">
                    {paginatedList.length > 0 ? (
                      paginatedList.map((record) => {
                        const isSelected = selectedRawRecord?.telecaller_id === record.telecaller_id;
                        const backendStatus = String(record.attendance_status || "").toLowerCase();
                        let statusText = "Absent";
                        let statusClass = "absent";

                        if (backendStatus === "active") {
                          statusText = "Present";
                          statusClass = "present";
                        } else if (backendStatus === "weekly_off") {
                          statusText = "Weekly Off";
                          statusClass = "weeklyoff";
                        } else if (backendStatus === "leave" || backendStatus === "on_leave") {
                          statusText = "On Leave";
                          statusClass = "onleave";
                        }

                        const totals = record.totals || {};

                        return (
                          <div
                            key={record.telecaller_id}
                            className={`attendanceItemCard agencyItemCard ${isSelected ? "selectedCard" : ""}`}
                            onClick={() => {
                              setSelectedTelecallerId(record.telecaller_id);
                            }}
                          >
                            {/* Avatar and Name */}
                            <div className="agencyUserInfo">
                              <div
                                className="userAvatar"
                                style={{ backgroundColor: getAvatarColor(record.telecaller_id) }}
                              >
                                {getInitials(record.telecaller_name)}
                              </div>
                              <div className="userNameBlock">
                                <span className="userName">{record.telecaller_name}</span>
                                <span className="userId">ID: {record.telecaller_id}</span>
                              </div>
                            </div>

                            {/* 2x2 Grid metrics */}
                            <div className="agencyDurationGrid">
                              <div className="durationCell">
                                <span className="durationLabel">Work:</span>
                                <span className="durationVal highlight">{totals.total_work_duration || "0h 0m"}</span>
                              </div>
                              <div className="durationCell">
                                <span className="durationLabel">Break:</span>
                                <span className="durationVal">{totals.total_break_duration || "0h 0m"}</span>
                              </div>
                              <div className="durationCell">
                                <span className="durationLabel">Lunch:</span>
                                <span className="durationVal">{totals.lunch_break_duration || "0h 0m"}</span>
                              </div>
                              <div className="durationCell">
                                <span className="durationLabel">Tea:</span>
                                <span className="durationVal">{totals.tea_break_duration || "0h 0m"}</span>
                              </div>
                            </div>

                            {/* Status pill */}
                            <div className="cardStatusBlock">
                              <span className={`statusPill ${statusClass}`}>
                                <span className="statusDot" />
                                {statusText}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="emptyListState">
                        <div className="emptyTitle">No telecaller records found</div>
                        <div className="emptyDesc">Try a different name or search query.</div>
                      </div>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="paginationContainer">
                      <button
                        className="paginationBtn"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Back
                      </button>
                      <span className="paginationInfo">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="paginationBtn"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </Card>
          </Col>

          {/* Activity Log Sidebar */}
          <Col xs={24} xl={8}>
            <Card className="attendanceTimelineCard" bordered={false}>
              <div className="timelineHeader">
                <span className="timelineTitle">
                  Day Activity Log
                </span>
                <span className="timelineDate">{currentDisplayDate || "No Date"}</span>
              </div>

              <div className="timelineScrollContainer">
                {timelineEvents.length > 0 ? (
                  <Timeline className="customTimeline">
                    {timelineEvents.map((evt, index) => {
                      let dotColor = "gray";
                      let icon = null;

                      if (evt.type === "login") {
                        dotColor = "#52c41a";
                      } else if (evt.type === "logout") {
                        dotColor = "#1890ff";
                      } else if (evt.type === "break_start" || evt.type === "break") {
                        dotColor = "#faad14";
                        icon = <CoffeeOutlined style={{ fontSize: '12px', color: '#faad14' }} />;
                      } else if (evt.type === "break_end") {
                        dotColor = "#d85b2b";
                      } else if (evt.type === "active") {
                        dotColor = "#f5222d";
                      }

                      return (
                        <Timeline.Item
                          key={index}
                          dot={icon}
                          color={dotColor}
                        >
                          <div className="timelineEventDetail">
                            <div className="timelineEventHeader">
                              <span className="eventTitle">{evt.title}</span>
                              <span className="timeLabel">{evt.timeStr}</span>
                            </div>
                            <div className="eventDesc">{evt.desc}</div>
                          </div>
                        </Timeline.Item>
                      );
                    })}
                  </Timeline>
                ) : (
                  <div className="emptyTimeline">
                    <div className="emptyTitle">No activity logged for this day</div>
                    <div className="emptyDesc">Weekly Off, Leave, Absent, or no logged sessions.</div>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Telecaller Dashboard View
  return (
    <div className="attendancePageContainer animate-fade-in">
      <div className="attendanceHeader">
        <h1 className="attendanceTitle">Attendance Summary</h1>
        <DatePicker
          value={filterDate}
          onChange={(date) => setFilterDate(date)}
          disabledDate={(current) => current && current > dayjs().endOf("day")}
          placeholder="Select Date"
          format="DD MMM YYYY"
          className="attendanceDatePicker"
        />
      </div>

      {/* Grid of Custom Cards and Details Timeline */}
      <Row gutter={[24, 24]} className="attendanceGridContainer">
        <Col xs={24} xl={16}>
          <Card className="attendanceTableCard" bordered={false}>
            <div className="tableTitle">Daily Attendance Details</div>
            {loading ? (
              <div className="loadingContainer">
                <Spin size="large" />
              </div>
            ) : (
              <div className="dailyListContainer">
                {displayList.length > 0 ? (
                  displayList.map((record) => {
                    const isSelected = selectedDate === record.date || (!selectedDate && displayList[0]?.date === record.date);
                    const isWeeklyOff = record.status === "Weekly Off" || record.status === "On Leave" || record.status === "Absent";

                    return (
                      <div
                        key={record.key}
                        className={`attendanceItemCard ${isSelected ? "selectedCard" : ""} ${isWeeklyOff ? "weeklyOffCard" : ""}`}
                        onClick={() => {
                          if (record.status !== "Weekly Off" && record.status !== "On Leave" && record.status !== "Absent") {
                            setSelectedDate(record.date);
                          }
                        }}
                      >
                        {/* Left: Date Display */}
                        <div className="cardDateBlock">
                          <span className="dateDayNum">{moment(record.date).format("DD")}</span>
                          <div className="dateMonthYear">
                            <span className="dateMonth">{moment(record.date).format("MMM")}</span>
                            <span className="dateDayName">{record.day}</span>
                          </div>
                        </div>

                        {/* Middle: Shift Durations */}
                        <div className="cardShiftInfo">
                          <div className="shiftHoursRow">
                            <div className="infoItem">
                              <span className="infoLabel">In:</span>
                              <span className="infoValue">{record.checkIn}</span>
                            </div>
                            <div className="infoItem">
                              <span className="infoLabel">Out:</span>
                              <span className="infoValue">{record.checkOut}</span>
                            </div>
                            <div className="infoItem">
                              <span className="infoLabel">Work:</span>
                              <span className="infoValue highlightValue">{record.workHours}</span>
                            </div>
                            <div className="infoItem">
                              <span className="infoLabel">Break:</span>
                              <span className="infoValue">{record.breakDuration}</span>
                            </div>
                          </div>
                        </div>

                        {/* Right: Status Tag */}
                        <div className="cardStatusBlock">
                          <span className={`statusPill ${record.status.toLowerCase().replace(/\s+/g, "")}`}>
                            <span className="statusDot" />
                            {record.status}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="emptyListState">
                    <div className="emptyTitle">No attendance records found</div>
                    <div className="emptyDesc">Select a different date range.</div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card className="attendanceTimelineCard" bordered={false}>
            <div className="timelineHeader">
              <span className="timelineTitle">Day Activity Log</span>
              <span className="timelineDate">{currentDisplayDate}</span>
            </div>

            <div className="timelineScrollContainer">
              {timelineEvents.length > 0 ? (
                <Timeline className="customTimeline">
                  {timelineEvents.map((evt, index) => {
                    let dotColor = "gray";
                    let icon = null;

                    if (evt.type === "login") {
                      dotColor = "#52c41a";
                    } else if (evt.type === "logout") {
                      dotColor = "#1890ff";
                    } else if (evt.type === "break_start" || evt.type === "break") {
                      dotColor = "#faad14";
                      icon = <CoffeeOutlined style={{ fontSize: '12px', color: '#faad14' }} />;
                    } else if (evt.type === "break_end") {
                      dotColor = "#d85b2b";
                    } else if (evt.type === "active") {
                      dotColor = "#f5222d";
                    }

                    return (
                      <Timeline.Item
                        key={index}
                        dot={icon}
                        color={dotColor}
                      >
                        <div className="timelineEventDetail">
                          <div className="timelineEventHeader">
                            <span className="eventTitle">{evt.title}</span>
                            <span className="timeLabel">{evt.timeStr}</span>
                          </div>
                          <div className="eventDesc">{evt.desc}</div>
                        </div>
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              ) : (
                <div className="emptyTimeline">
                  <div className="emptyTitle">No activity logged for this day</div>
                  <div className="emptyDesc">Weekly Off, Leave, or no logged sessions.</div>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AttendancePage;
